/* Cavrix Core — public site enhancements. Everything works without this file;
   it adds the menu behaviour, the hero video, one-shot scroll reveals, logo
   fallbacks and inline contact-form validation. No third-party code, no tracking. */
(function () {
  "use strict";

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------- Header & mobile menu ---------------- */
  function initHeader() {
    var header = document.getElementById("site-header");
    if (!header) return;
    // Update the scrolled state at most once per frame, and only when it changes.
    var scrolled = null, ticking = false;
    var update = function () {
      ticking = false;
      var now = window.scrollY > 8;
      if (now !== scrolled) { scrolled = now; header.toggleAttribute("data-scrolled", now); }
    };
    update();
    window.addEventListener("scroll", function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });

    var toggle = header.querySelector("[data-menu-toggle]");
    var menu = document.getElementById("mobile-menu");
    var label = header.querySelector("[data-menu-label]");
    if (!toggle || !menu) return;

    function setOpen(open, returnFocus) {
      toggle.setAttribute("aria-expanded", String(open));
      menu.hidden = !open;
      label.textContent = open ? "Close menu" : "Open menu";
      document.documentElement.toggleAttribute("data-menu-open", open);
      if (open) {
        var first = menu.querySelector("a");
        if (first) first.focus();
      } else if (returnFocus) {
        toggle.focus();
      }
    }
    toggle.addEventListener("click", function () { setOpen(toggle.getAttribute("aria-expanded") !== "true", true); });
    document.addEventListener("keydown", function (e) {
      if (menu.hidden) return;
      if (e.key === "Escape") { e.preventDefault(); setOpen(false, true); return; }
      if (e.key !== "Tab") return;
      var items = [toggle].concat(Array.prototype.slice.call(menu.querySelectorAll("a")));
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    window.matchMedia("(min-width: 960px)").addEventListener("change", function (mq) { if (mq.matches) setOpen(false, false); });
  }

  /* ---------------- FadingVideo ----------------
     Reusable background video for any [data-video-scope] containing
     video.hero-video[data-src] (one URL, or a JSON array of URLs to play in turn).
     - starts hidden and fades in once frames are ready;
     - fades out just before the end of each pass, then replays (or advances);
     - never downloads before the section is near the viewport, pauses when it
       is off-screen or the tab is hidden, and respects reduced motion / data saver;
     - a failed source falls back to the next one, then to the static backdrop;
     - the [data-video-toggle] button always reflects the real playback state. */
  var FADE_OUT_SECONDS = 0.7;

  function initFadingVideo(scope) {
    var video = scope.querySelector("video.hero-video[data-src]");
    if (!video) return;
    var toggle = scope.querySelector("[data-video-toggle]");
    var raw = video.getAttribute("data-src");
    var sources;
    try { sources = raw.charAt(0) === "[" ? JSON.parse(raw) : [raw]; } catch (e) { sources = [raw]; }
    var index = 0, userPaused = false, inView = false, failed = 0, raf = 0, dead = false;

    function syncToggle() {
      if (!toggle || dead) return;
      toggle.hidden = false;
      toggle.setAttribute("aria-pressed", String(video.paused));
    }
    function giveUp() {
      dead = true;
      scope.classList.add("hero--video-failed");
      video.classList.remove("is-playing", "is-fading");
      video.removeAttribute("src");
      if (toggle) toggle.hidden = true;
      cancelAnimationFrame(raf);
    }
    function load(i) {
      index = i % sources.length;
      video.src = sources[index];
    }
    function play() {
      if (dead) return;
      if (!video.getAttribute("src")) load(index);
      var p = video.play();
      if (p && p.catch) {
        p.catch(function (err) {
          // Autoplay blocked by the browser is not an asset failure: keep the backdrop and offer Play.
          if (err && err.name === "NotAllowedError") { userPaused = true; syncToggle(); }
        });
      }
    }
    // Watch the clock only while playing, to start the fade-out just before the end.
    function tick() {
      if (video.paused) return;
      var d = video.duration;
      if (d && isFinite(d) && d - video.currentTime <= FADE_OUT_SECONDS) {
        video.classList.add("is-fading");
        video.classList.remove("is-playing");
      }
      raf = requestAnimationFrame(tick);
    }

    video.addEventListener("loadeddata", function () { failed = 0; });
    video.addEventListener("playing", function () {
      video.classList.remove("is-fading");
      video.classList.add("is-playing");
      syncToggle();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    });
    video.addEventListener("pause", function () { cancelAnimationFrame(raf); syncToggle(); });
    video.addEventListener("ended", function () {
      if (sources.length > 1) { load(index + 1); play(); }
      else { video.currentTime = 0; play(); }
    });
    video.addEventListener("error", function () {
      if (dead) return;
      if (window.console) console.warn("[video] Could not be loaded:", sources[index]);
      failed++;
      if (failed >= sources.length) giveUp();
      else { load(index + 1); if (inView && !userPaused) play(); }
    });

    if (toggle) {
      toggle.addEventListener("click", function () {
        if (video.paused) { userPaused = false; play(); }
        else { userPaused = true; video.pause(); }
      });
    }

    // Respect reduced motion, data saver and slow connections: keep the static backdrop and offer Play.
    var conn = navigator.connection || {};
    var slow = conn.saveData || /(^|-)2g|3g/.test(conn.effectiveType || "");
    if (reducedMotion || slow) {
      userPaused = true;
      syncToggle();
      return;
    }
    // Start only after the page itself has finished loading, so the video never competes with it.
    function watch() {
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          if (!inView) { if (!video.paused) video.pause(); }
          else if (!userPaused && !document.hidden) play();
        }, { threshold: 0.05 }).observe(scope);
      } else {
        inView = true;
        play();
      }
      document.addEventListener("visibilitychange", function () {
        if (document.hidden) { if (!video.paused) video.pause(); }
        else if (inView && !userPaused) play();
      });
    }
    if (document.readyState === "complete") watch();
    else window.addEventListener("load", function () { setTimeout(watch, 300); }, { once: true });
  }

  /* ---------------- Footer mark ----------------
     "Cavrix Core" rises letter by letter, once, as the footer comes into view. */
  function initFooterMark() {
    var mark = document.querySelector("[data-footer-mark]");
    if (!mark || reducedMotion || !("IntersectionObserver" in window)) return;
    var c = 0;
    Array.prototype.forEach.call(mark.querySelectorAll(".footer-mark-line"), function (line) {
      var text = line.textContent;
      line.textContent = "";
      Array.prototype.forEach.call(text, function (ch) {
        var span = document.createElement("span");
        span.className = "fm-ch";
        span.style.setProperty("--c", String(c++));
        span.textContent = ch;
        line.appendChild(span);
      });
    });
    mark.classList.add("fm-ready");
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { mark.classList.add("is-in"); io.disconnect(); }
    }, { threshold: 0.25 });
    io.observe(mark);
  }

  /* ---------------- BlurText ----------------
     Splits the text of [data-blur-text] into word spans (keeping spaces and
     inline elements, so wrapping and the accessible text are unchanged), then
     clears the words in sequence once, when the heading first enters the view. */
  function initBlurText() {
    var els = document.querySelectorAll("[data-blur-text]");
    if (!els.length || reducedMotion || !("IntersectionObserver" in window)) return;
    Array.prototype.forEach.call(els, function (el) {
      var n = 0;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      var texts = [];
      while (walker.nextNode()) texts.push(walker.currentNode);
      texts.forEach(function (node) {
        var parts = node.nodeValue.split(/(\s+)/);
        if (parts.length === 1 && !parts[0].trim()) return;
        var frag = document.createDocumentFragment();
        parts.forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          var span = document.createElement("span");
          span.className = "bt-word";
          span.style.setProperty("--w", String(n++));
          span.textContent = part;
          frag.appendChild(span);
        });
        node.parentNode.replaceChild(frag, node);
      });
      el.classList.add("bt-ready");
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); }
      });
    }, { threshold: 0.2 });
    Array.prototype.forEach.call(els, function (el) { io.observe(el); });
  }

  /* ---------------- One-shot reveal on scroll ---------------- */
  // Content is fully visible without JavaScript or with reduced motion; the
  // CSS only hides .reveal elements while html.js is set and motion is allowed.
  function initReveal() {
    var items = document.querySelectorAll(".reveal");
    if (!items.length || reducedMotion || !("IntersectionObserver" in window)) return;
    // Only content that starts below the visible screen is hidden and revealed later, so nothing
    // has to run before the first paint and nothing already on screen ever flickers.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.01 });
    var fold = window.innerHeight;
    Array.prototype.forEach.call(items, function (el) {
      if (el.getBoundingClientRect().top > fold) {
        el.classList.add("is-armed");
        io.observe(el);
      }
    });
  }


  /* ---------------- Logo fallback ---------------- */
  function initLogos() {
    var imgs = document.querySelectorAll("img[data-logo]");
    Array.prototype.forEach.call(imgs, function (img) {
      function failed() {
        var plate = img.closest(".logo-plate");
        if (!plate || plate.getAttribute("data-logo-state") === "failed") return;
        plate.setAttribute("data-logo-state", "failed");
        plate.setAttribute("data-bg", "dark");
        img.hidden = true;
        var fb = plate.querySelector(".logo-fallback");
        if (fb) {
          fb.hidden = false;
          if (img.alt) { plate.setAttribute("role", "img"); plate.setAttribute("aria-label", img.alt); }
        }
        if (window.console) console.warn("[brand-logo] Logo failed to load:", img.currentSrc || img.src);
      }
      if (img.complete && img.naturalWidth === 0) failed();
      else img.addEventListener("error", failed);
    });
  }

  /* ---------------- Contact form ---------------- */
  function initContact() {
    var form = document.querySelector("[data-contact-form]");
    if (!form) return;
    var wrap = form.closest("[data-contact]");
    var message = form.querySelector("#f-message");
    var count = form.querySelector("[data-count]");
    var live = form.querySelector("[data-live]");
    var submit = form.querySelector("[data-submit]");
    var submitLabel = form.querySelector("[data-submit-label]");
    var order = ["name", "email", "inquiryType", "subject", "message", "consent"];
    var emailRe = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]{2,}$/;

    if (message && count) message.addEventListener("input", function () { count.textContent = message.value.length.toLocaleString(); });

    // The page is static HTML, so a signed, time-stamped anti-spam token is fetched from send.php.
    var tokenInput = form.querySelector("[data-form-token]");
    function loadToken() {
      if (!tokenInput) return Promise.resolve();
      return fetch("/send.php?token=1", { credentials: "same-origin", cache: "no-store" })
        .then(function (r) { return r.json(); })
        .then(function (d) { if (d && d.token) tokenInput.value = d.token; })
        .catch(function () {});
    }
    loadToken();

    function field(name) { return form.querySelector('[name="' + name + '"]'); }
    function clearErrors() {
      Array.prototype.forEach.call(form.querySelectorAll(".field-error[data-js]"), function (n) { n.remove(); });
      Array.prototype.forEach.call(form.querySelectorAll("[aria-invalid]"), function (n) {
        n.removeAttribute("aria-invalid");
        var d = (n.getAttribute("aria-describedby") || "").split(" ").filter(function (id) { return !/-error$/.test(id); }).join(" ");
        if (d) n.setAttribute("aria-describedby", d); else n.removeAttribute("aria-describedby");
      });
      var old = form.querySelector("[data-error-summary]");
      if (old) old.remove();
    }
    function showErrors(errors) {
      clearErrors();
      var keys = order.filter(function (k) { return errors[k]; });
      keys.forEach(function (k) {
        var el = field(k);
        if (!el) return;
        var id = "f-" + k + "-error";
        var p = document.createElement("p");
        p.className = "field-error";
        p.id = id;
        p.setAttribute("data-js", "");
        p.textContent = errors[k];
        var host = k === "consent" ? el.closest(".field") : el.parentNode;
        host.appendChild(p);
        el.setAttribute("aria-invalid", "true");
        var d = el.getAttribute("aria-describedby");
        el.setAttribute("aria-describedby", (d ? d + " " : "") + id);
      });
      if (keys.length > 1) {
        var box = document.createElement("div");
        box.className = "notice notice-error";
        box.setAttribute("role", "alert");
        box.setAttribute("data-error-summary", "");
        var strong = document.createElement("p");
        strong.innerHTML = "<strong></strong>";
        strong.firstChild.textContent = "Please fix " + keys.length + " fields:";
        var ul = document.createElement("ul");
        ul.className = "error-summary";
        keys.forEach(function (k) {
          var li = document.createElement("li"), a = document.createElement("a");
          a.href = "#f-" + k; a.textContent = errors[k];
          li.appendChild(a); ul.appendChild(li);
        });
        var inner = document.createElement("div");
        inner.appendChild(strong); inner.appendChild(ul); box.appendChild(inner);
        form.insertBefore(box, form.children[1]);
      }
      if (keys.length) field(keys[0]).focus();
    }
    function validate() {
      var e = {};
      var name = field("name").value.trim();
      var email = field("email").value.trim();
      var msg = message.value.trim();
      if (!name) e.name = "Please enter your name.";
      else if (name.length > 100) e.name = "Name must be 100 characters or fewer.";
      if (!email) e.email = "Please enter your email address.";
      else if (!emailRe.test(email)) e.email = "Enter a valid email address.";
      if (!field("inquiryType").value) e.inquiryType = "Choose an inquiry type.";
      if (field("subject").value.trim().length > 150) e.subject = "Subject must be 150 characters or fewer.";
      if (!msg) e.message = "Please enter a message.";
      else if (msg.length < 10) e.message = "Message must be at least 10 characters.";
      else if (msg.length > 5000) e.message = "Message must be 5,000 characters or fewer.";
      if (!field("consent").checked) e.consent = "Please confirm you have read the privacy policy.";
      return e;
    }
    function showResult(r) {
      var ok = r.ok;
      var tone = ok ? (r.notification === "failed" ? "warn" : "success") : "error";
      var box = document.createElement("div");
      box.className = "notice notice-" + tone + (ok ? " form-result" : "");
      box.setAttribute("role", ok ? "status" : "alert");
      box.tabIndex = -1;
      var inner = document.createElement("div");
      if (ok) {
        var t = document.createElement("p");
        t.className = "form-result-title";
        t.textContent = tone === "success" ? "Message received" : "Message saved — notification delayed";
        inner.appendChild(t);
      }
      var m = document.createElement("p"); m.textContent = r.message; inner.appendChild(m);
      if (ok && r.ref) { var ref = document.createElement("p"); ref.className = "form-result-ref"; ref.textContent = "Reference: " + r.ref; inner.appendChild(ref); }
      box.appendChild(inner);
      var prev = wrap.querySelector("[data-js-result]");
      if (prev) prev.remove();
      box.setAttribute("data-js-result", "");
      if (ok) { form.replaceWith(box); }
      else { form.insertBefore(box, form.children[1]); }
      box.focus();
    }

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      if (submit.disabled) return;
      var errors = validate();
      if (Object.keys(errors).length) { showErrors(errors); return; }
      clearErrors();
      var data = {};
      new FormData(form).forEach(function (v, k) { data[k] = v; });
      data.consent = field("consent").checked ? "true" : "";
      submit.disabled = true;
      submit.setAttribute("aria-disabled", "true");
      var original = submitLabel.innerHTML;
      submitLabel.innerHTML = '<span class="spinner" aria-hidden="true"></span> Sending…';
      if (live) live.textContent = "Sending your message…";
      (tokenInput && !tokenInput.value ? loadToken() : Promise.resolve())
        .then(function () {
          data.formToken = tokenInput ? tokenInput.value : "";
          return fetch("/send.php", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(data), credentials: "same-origin" });
        })
        .then(function (res) { return res.json().catch(function () { return { ok: false, code: "delivery_failed", message: "Something went wrong and we couldn't confirm your message was received. Please email hello@cavrixcore.com." }; }); })
        .catch(function () { return { ok: false, code: "network", message: "We couldn't reach the server — please check your connection and try again, or email hello@cavrixcore.com." }; })
        .then(function (r) {
          submit.disabled = false;
          submit.removeAttribute("aria-disabled");
          submitLabel.innerHTML = original;
          if (live) live.textContent = "";
          if (!r.ok && r.code === "validation" && r.errors) showErrors(r.errors);
          else showResult(r);
        });
    });
  }

  function init() {
    initHeader();
    Array.prototype.forEach.call(document.querySelectorAll("[data-video-scope]"), initFadingVideo);
    initBlurText();
    initFooterMark();
    var year = document.querySelector("[data-year]");
    if (year) year.textContent = String(new Date().getFullYear());
    initReveal();
    initLogos();
    initContact();
    var result = document.querySelector("[data-result]");
    if (result) result.focus();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
